import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
  CircularProgress,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import {
  getAllStoriesAdmin,
  createStory,
  updateStory,
  deleteStory,
} from '../../services/successStoryService';

const emptyStory = {
  title: '',
  brandName: '',
  brandLogo: '',
  summary: '',
  content: '',
  tags: '',
  featured: false,
  isPublished: false,
  order: 0,
  uplift: 0,
  testCount: 0,
  winRate: 0,
  revenueImpact: '',
};

const SuccessStoryManagement = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyStory);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchStories = async () => {
    try {
      setLoading(true);
      const data = await getAllStoriesAdmin();
      setStories(data);
    } catch {
      setSnackbar({ open: true, message: 'Hikayeler yüklenemedi', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStories();
  }, []);

  const openDialog = (story = null) => {
    if (story) {
      setEditingId(story._id);
      setForm({
        title: story.title,
        brandName: story.brandName,
        brandLogo: story.brandLogo || '',
        summary: story.summary,
        content: story.content || '',
        tags: (story.tags || []).join(', '),
        featured: story.featured,
        isPublished: story.isPublished,
        order: story.order || 0,
        uplift: story.metrics?.uplift || 0,
        testCount: story.metrics?.testCount || 0,
        winRate: story.metrics?.winRate || 0,
        revenueImpact: story.metrics?.revenueImpact || '',
      });
    } else {
      setEditingId(null);
      setForm(emptyStory);
    }
    setOpen(true);
  };

  const buildPayload = () => ({
    title: form.title,
    brandName: form.brandName,
    brandLogo: form.brandLogo,
    summary: form.summary,
    content: form.content,
    tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
    featured: form.featured,
    isPublished: form.isPublished,
    order: Number(form.order) || 0,
    metrics: {
      uplift: Number(form.uplift) || 0,
      testCount: Number(form.testCount) || 0,
      winRate: Number(form.winRate) || 0,
      revenueImpact: form.revenueImpact,
    },
  });

  const handleSave = async () => {
    try {
      const payload = buildPayload();
      if (editingId) {
        await updateStory(editingId, payload);
      } else {
        await createStory(payload);
      }
      setSnackbar({ open: true, message: 'Kaydedildi', severity: 'success' });
      setOpen(false);
      fetchStories();
    } catch {
      setSnackbar({ open: true, message: 'Kayıt başarısız', severity: 'error' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu hikayeyi silmek istediğinize emin misiniz?')) return;
    try {
      await deleteStory(id);
      fetchStories();
      setSnackbar({ open: true, message: 'Silindi', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Silme başarısız', severity: 'error' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>Başarı Hikayeleri</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => openDialog()}>Yeni Hikaye</Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Başlık</TableCell>
              <TableCell>Marka</TableCell>
              <TableCell>Uplift</TableCell>
              <TableCell>Yayında</TableCell>
              <TableCell>Öne Çıkan</TableCell>
              <TableCell align="right">İşlemler</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {stories.map((s) => (
              <TableRow key={s._id}>
                <TableCell>{s.title}</TableCell>
                <TableCell>{s.brandName}</TableCell>
                <TableCell>{s.metrics?.uplift ? `+%${s.metrics.uplift}` : '-'}</TableCell>
                <TableCell>{s.isPublished ? 'Evet' : 'Hayır'}</TableCell>
                <TableCell>{s.featured ? 'Evet' : 'Hayır'}</TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => openDialog(s)}><Edit /></IconButton>
                  <IconButton color="error" onClick={() => handleDelete(s._id)}><Delete /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingId ? 'Hikaye Düzenle' : 'Yeni Hikaye'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField label="Başlık" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} fullWidth />
          <TextField label="Marka Adı" value={form.brandName} onChange={(e) => setForm({ ...form, brandName: e.target.value })} fullWidth />
          <TextField label="Özet" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} fullWidth multiline rows={2} />
          <TextField label="İçerik (## başlık destekli)" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} fullWidth multiline rows={6} />
          <TextField label="Etiketler (virgülle)" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} fullWidth />
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField label="Uplift %" type="number" value={form.uplift} onChange={(e) => setForm({ ...form, uplift: e.target.value })} sx={{ width: 120 }} />
            <TextField label="Test Sayısı" type="number" value={form.testCount} onChange={(e) => setForm({ ...form, testCount: e.target.value })} sx={{ width: 120 }} />
            <TextField label="Win Rate %" type="number" value={form.winRate} onChange={(e) => setForm({ ...form, winRate: e.target.value })} sx={{ width: 120 }} />
            <TextField label="Revenue Impact" value={form.revenueImpact} onChange={(e) => setForm({ ...form, revenueImpact: e.target.value })} sx={{ flex: 1, minWidth: 160 }} />
            <TextField label="Sıra" type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} sx={{ width: 80 }} />
          </Box>
          <FormControlLabel control={<Checkbox checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} />} label="Anasayfada öne çıkar" />
          <FormControlLabel control={<Checkbox checked={form.isPublished} onChange={(e) => setForm({ ...form, isPublished: e.target.checked })} />} label="Yayınla" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>İptal</Button>
          <Button variant="contained" onClick={handleSave}>Kaydet</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default SuccessStoryManagement;
